import {
  breadcrumb,
  isTelemetryReady,
  telemetryViewerContext,
  track,
} from '../services/telemetry';

/**
 * Diagnostic instrumentation for the intermittent INTERNAL_ADMIN "Access
 * denied" investigation. It synchronously snapshots the auth/scope
 * localStorage state + current URL at the instant a guard redirects to
 * `/error` (and again when the `/error` page renders).
 *
 * Capture vs. emission are deliberately split:
 *
 *   - CAPTURE (`recordAccessDenialDiag`) writes the snapshot to localStorage.
 *     This is synchronous and init-independent, so it survives the
 *     redirect/reload that follows a denial — the durable copy.
 *
 *   - EMISSION (`flushAccessDenialDiagToLogRocket`) ships any captured snapshot
 *     to LogRocket, but ONLY once LogRocket has been initialised. Every denial
 *     site runs before LogRocket is ready: the guards redirect during render /
 *     just before `window.location.reload()`, and the `/error` page-render
 *     effect fires before `AuthenticatedShell`'s `startTelemetry()` effect
 *     (React runs child effects before parent). LogRocket only buffers calls
 *     made *after* init, and a denial is followed by a redirect or reload that
 *     loses anything held in memory. Deferring emission to a post-init flush
 *     is what makes the data reach the replay console and the Custom Events
 *     filter.
 *
 * localStorage keys written:
 *   - `lastAccessDenial`     — written by the guard that triggers the redirect
 *   - `lastErrorPageRender`  — written by the /error page on mount
 *
 * NOT temporary, despite this file's origin as a spike.
 *
 * Measured 2026-09-09 over 30 days of prod, by the `source` field:
 * `EntityScopeGuard` 27 sessions, `ProtectedRoute` 1 — still firing the
 * previous day. "Root cause confirmed" in the original comment meant
 * *diagnosed*, not *fixed*, and the two are not the same state.
 *
 * The volume driver was the deep-link resolution path: EntityScopeGuard had no
 * sub-entity fallback, so the same link resolved or denied depending on
 * whether an org happened to be selected when it arrived. DV-3012 BC-8 fixes
 * that structurally, and these call sites are how we know whether it worked.
 *
 * RETIREMENT CONDITION (not a vague "when confirmed", which is how this
 * survived a month): BC-8 shipped, AND the `resolveEntityScope`-sourced
 * `access-denied-redirect` count at rest for one full release cycle. Then
 * delete this file, the ONE call in `entity-scope-denial.ts` (it funnels all
 * three former denial sites), the one in `App.tsx`'s `ProtectedRoute`, the
 * ones in `unauthorized.tsx`, and both `access-denied-*` events in
 * `config/telemetry-events.ts`.
 */
const AUTH_SNAPSHOT_KEYS = [
  'role',
  'defaultRole',
  'currentContext',
  'selectedOrg',
  'selectedEntity',
  'parentEntityId',
  'intendedUrl',
] as const;

/**
 * The snapshot localStorage keys and the LogRocket event name each maps to.
 * The flush owns the key→event mapping so the writers don't have to know about
 * LogRocket timing at all.
 */
export const ACCESS_DENIAL_DIAG_KEYS = {
  lastAccessDenial: 'access-denied-redirect',
  lastErrorPageRender: 'access-denied-page-render',
} as const;

type SnapshotEntry = Record<string, string>;

const buildEntry = (
  event: string,
  extra: Record<string, unknown>,
): SnapshotEntry => {
  const entry: SnapshotEntry = {
    // The role and account at the denial. `track()` stamps the scope at send,
    // which is after the redirect or reload, often in another account or none;
    // these keep the event's scope fields true to the denial.
    ...Object.fromEntries(
      Object.entries(telemetryViewerContext()).map(([k, v]) => [k, String(v)]),
    ),
    event,
    ...Object.fromEntries(
      Object.entries(extra).map(([k, v]) => [k, String(v ?? '')]),
    ),
    url: window.location.pathname + window.location.search,
    at: new Date().toISOString(),
  };
  for (const key of AUTH_SNAPSHOT_KEYS) {
    entry[key] = localStorage.getItem(key) ?? '';
  }
  return entry;
};

/**
 * Captures an access-denial snapshot to localStorage. Does NOT touch LogRocket
 * — emission is deferred to `flushAccessDenialDiagToLogRocket` (see file
 * header). `storageKey` must be one of `ACCESS_DENIAL_DIAG_KEYS`; `message` is
 * the LogRocket event name the flush will emit under.
 */
export const recordAccessDenialDiag = (
  storageKey: keyof typeof ACCESS_DENIAL_DIAG_KEYS,
  message: string,
  extra: Record<string, unknown>,
): void => {
  const entry = buildEntry(message, extra);
  try {
    localStorage.setItem(storageKey, JSON.stringify(entry));
  } catch {
    // Diagnostics must never break the page (quota / serialization).
  }
};

/**
 * Drains any captured access-denial snapshots from localStorage into LogRocket.
 * Safe to call from multiple sites (post-init in `AuthenticatedShell`, and on the
 * `/error` page): it no-ops until LogRocket is ready, and dedupes by the
 * snapshot's `at` timestamp so repeated calls / reloads don't re-emit the same
 * denial. The snapshot itself is left in place as a device-readable fallback.
 */
export const flushAccessDenialDiagToLogRocket = (): void => {
  // Until LogRocket is initialised the snapshot stays in localStorage for the
  // next attempt. Telemetry would hold the event for init too, but only in
  // memory: marking it emitted then would lose it to the reload that follows.
  if (!isTelemetryReady()) return;
  flushSnapshot('lastAccessDenial');
  flushSnapshot('lastErrorPageRender');
};

const flushSnapshot = (storageKey: keyof typeof ACCESS_DENIAL_DIAG_KEYS): void => {
  const event = ACCESS_DENIAL_DIAG_KEYS[storageKey];
  const raw = localStorage.getItem(storageKey);
  if (!raw) return;

  try {
    const entry = JSON.parse(raw) as SnapshotEntry;
    const emittedMarkerKey = `${storageKey}EmittedAt`;
    if (entry.at && localStorage.getItem(emittedMarkerKey) === entry.at) {
      return; // already emitted this exact snapshot
    }

    // console.warn: visible in the user's own devtools AND captured into the
    // LogRocket replay console feed (LogRocket instruments console at init).
    console.warn(`[access-denial] ${event}`, entry);
    // breadcrumb: inline + expandable in the replay console at the denial instant.
    // track: filterable Custom Event so the session is findable by user name.
    breadcrumb(event, entry);
    const sent = track(event, entry);

    // Left unmarked when nothing went out, so the next flush tries again.
    if (sent && entry.at) localStorage.setItem(emittedMarkerKey, entry.at);
  } catch {
    // Malformed snapshot — leave it for the next attempt.
  }
};
